import {
  UserStatus,
  createUser,
  getUser,
  APIUser,
  PixelStreamingExperience,
  getPartnerUser,
  getUserStatusErrorMessage,
  PixelStreamingExperienceDisabledClass,
} from "../../utils/userEndpoints";

export async function getOrCreateUser(
  userId: string | undefined,
  email: string | undefined,
  resLinkParamsId: string | undefined,
  organizationId?: string,
): Promise<APIUser> {

  console.log("[getOrCreateUser] userId:", userId, " email:", email, "resLinkParamsId:", resLinkParamsId, "organizationId:", organizationId);

  if (!userId) {
    // We have a stytch user, but no userid: this means the user logged-in/created
    // via stytch, but we don't have a matching user in our system yet. So create one now.
    console.log("[getOrCreateUser] No user ID found in trusted metadata; creating.");
    const user = await createUser(organizationId, email, undefined, resLinkParamsId);
    console.log("[getOrCreateUser] Created user:", user);
    return user;
  } else {
    // We have a stytch user and a userId: check if the user exists in our system.
    const user = await getUser(userId!, resLinkParamsId, organizationId);
    return user;
  }
}

export function findAndSetActivePixelStreamingExperienceFromUser(
  userData: APIUser,
  resLinkParamsId: string | undefined,
): PixelStreamingExperience | undefined {
  if (!userData.supported_pixel_streaming_experiences) return undefined;

  let matchingExperience = null;

  if (resLinkParamsId) {
    matchingExperience = userData.supported_pixel_streaming_experiences.find(
      (exp) => exp.res_link_params_id === resLinkParamsId
    );
  } else {
    matchingExperience = userData.supported_pixel_streaming_experiences.find(
      (exp) => !exp.res_link_params_id || exp.res_link_params_id === null
    );
  }

  console.log("[findAndSetActivePixelStreamingExperienceFromUser] Found PixelStreamingExperience:", matchingExperience);

  return matchingExperience || undefined;
}

export type UserPartnerResult = {
  user: APIUser;
  experience: PixelStreamingExperience;
};

export async function getPartnerUserAndExperience(resLinkParamsId: string, organizationId: string): Promise<UserPartnerResult> {
  try {
    const user = await getPartnerUser(resLinkParamsId, organizationId);
    console.log("[getPartnerUserAndExperience] User found, proceeding with onboarding flow.", user);

    switch (user.status) {
      case UserStatus.ONBOARDING:
      case UserStatus.WAITLISTED:
      case UserStatus.SUSPENDED:
      case UserStatus.BANNED:
        throw new Error(getUserStatusErrorMessage(user.status));
      case UserStatus.ACTIVE:
        // we're good, continue
        break;
    }

    let experience = findAndSetActivePixelStreamingExperienceFromUser(
      user,
      resLinkParamsId
    );

    if (!experience) {
      throw new Error("Invalid link. Please check the link and try again.");
    }

    if (!experience.is_enabled) {
      let statusMsg = "";
      if (experience.disabled_rationale?.error_text) {
        statusMsg = experience.disabled_rationale.error_text;
      } else if (experience.disabled_rationale?.disabled_class) {
        switch (experience.disabled_rationale.disabled_class) {
          case PixelStreamingExperienceDisabledClass.LINK_CONSUMPTION_EXHAUSTED:
            statusMsg =
              "This is a one-time experience, so repeating it isn't possible. Thanks for being part of it!";
            break;
          default:
            statusMsg = "You do not have access to this experience.";
        }
      } else {
        statusMsg = "You do not have access to this experience.";
      }
      throw new Error(statusMsg);
    }

    return { user, experience };
  } catch (err) {
    console.error("[getPartnerUserAndExperience] Error in partner user flow:", err);
    throw err;
  }
}